<template lang="html">
  <v-sheet height="100%" color="bg1" width="100%" id="admin-zones-container">
    <v-sheet height="100%" class="rounded-tl-lg overflow-auto pa-4 d-flex flex-column">
      <v-row class="flex-grow-0 px-2">
        <v-col cols="12">
          <h3>Zones</h3>
          <div class="caption admin-subtitle">
            <template v-if="isCompany">
              Every zone of your facilities and facility groups. A group's zones are shared by all of its facilities.
            </template>
            <template v-else>
              This facility's zones and the ones its facility group shares.
            </template>
          </div>
        </v-col>
      </v-row>

      <v-row class="px-2">
        <v-col cols="12">
          <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px">
            <v-btn outlined color="primary" title="Show zone types" @click="typesDialog = true">
              <v-icon left>mdi-shape-outline</v-icon>Zone types
            </v-btn>
            <v-chip v-if="typeFilter" close outlined color="primary" @click:close="typeFilter = null">
              Type: {{ typeName(typeFilter) }}
            </v-chip>
            <!-- Company admin: narrow the list to one facility group or facility -->
            <v-select
              v-if="isCompany"
              v-model="ownerFilter"
              :items="ownerFilterItems"
              style="max-width: 260px"
              hide-details
              outlined
              dense
              prepend-inner-icon="mdi-filter-variant">
              <template v-slot:item="{ item }">
                <v-icon v-if="item.icon" small class="mr-2">{{ item.icon }}</v-icon>{{ item.text }}
              </template>
            </v-select>
            <v-text-field
              v-model="searchText"
              style="max-width: 300px"
              hide-details
              outlined
              dense
              clearable
              placeholder="Search zones..."
              prepend-inner-icon="mdi-magnify" />
            <v-switch v-model="showRetired" inset dense hide-details class="mt-0" label="Show retired" />
            <v-spacer />
            <v-btn v-if="canManage" color="primary" :disabled="!zoneTypes.length" @click="$refs.editZone.open()">
              <v-icon left>mdi-plus</v-icon>Add zone
            </v-btn>
          </div>

          <v-data-table
            :headers="headers"
            :items="tableZones"
            :search="searchText"
            :loading="loading"
            :items-per-page="15"
            :item-class="rowClass"
            class="elevation-1"
            @click:row="openRow">
            <template v-slot:[`item.name`]="{ item }">
              <div class="d-flex align-center">
                <span class="swatch mr-2" :style="{ backgroundColor: item.colour }"></span>
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="caption">{{ item.description }}</div>
                </div>
              </div>
            </template>
            <template v-slot:[`item.zoneTypeId`]="{ item }"><span>{{ typeName(item.zoneTypeId) }}</span></template>
            <template v-slot:[`item.ownerName`]="{ item }">
              <span class="owner-cell" :class="{ 'owner-cell--group': item.scope === 'group' }" :title="ownerTitle(item)">
                <v-icon x-small>{{ item.scope === 'group' ? 'mdi-group' : 'mdi-domain' }}</v-icon>
                {{ item.ownerName }}
              </span>
            </template>
            <template v-slot:[`item.boundary`]="{ item }">
              <span v-if="item.boundary" class="caption">
                {{ item.boundary.polygon.length }} pts · {{ item.boundary.zMin }}–{{ item.boundary.zMax }} m
              </span>
              <span v-else class="caption">—</span>
            </template>
            <template v-slot:[`item.planLevel`]="{ item }">
              <!-- Facility admin: opens the plan in the 2D side panel. Company page: no viewer, so it only tells. -->
              <v-tooltip v-if="item.planLevel" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="!isCompany"
                    icon
                    small
                    color="primary"
                    :aria-label="`Open the ${item.planLevel} plan`"
                    :loading="openingPlanId === item.id"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="openPlan(item)">
                    <v-icon small>mdi-floor-plan</v-icon>
                  </v-btn>
                  <v-icon v-else small color="primary" v-bind="attrs" v-on="on">mdi-floor-plan</v-icon>
                </template>
                <template v-if="!isCompany">Open the {{ item.planLevel }} plan</template>
                <template v-else>Boundary drawn on the {{ item.planLevel }} plan</template>
              </v-tooltip>
              <span v-else class="caption">—</span>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip v-if="item.status === 'retired'" x-small>Retired</v-chip>
              <v-chip v-else-if="item.retireOn" x-small outlined color="warning">Retirement scheduled</v-chip>
              <v-chip v-else x-small color="primary" text-color="white">Active</v-chip>
            </template>
            <template v-slot:[`item.retireDate`]="{ item }">
              <span v-if="item.retireDate" class="caption">{{ formatDate(item.retireDate) }}</span>
              <span v-else class="caption">—</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-menu v-if="canManage" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small :aria-label="`Actions for ${item.name}`" v-bind="attrs" v-on="on" @click.stop>
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="$refs.editZone.open(item)">
                    <v-list-item-icon class="mr-3"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.planLevel && !isCompany" @click="openPlan(item)">
                    <v-list-item-icon class="mr-3"><v-icon small>mdi-floor-plan</v-icon></v-list-item-icon>
                    <v-list-item-title>Open the {{ item.planLevel }} plan</v-list-item-title>
                  </v-list-item>
                  <v-divider class="my-1" />
                  <v-list-item v-if="item.status === 'active'" @click="openRetire(item)">
                    <v-list-item-icon class="mr-3"><v-icon small>mdi-archive-arrow-down-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ item.retireOn ? 'Change retirement' : 'Retire' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.retireOn" @click="setStatus(item, 'active')">
                    <v-list-item-icon class="mr-3"><v-icon small>mdi-calendar-remove-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Cancel scheduled retirement</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'retired'" @click="setStatus(item, 'active')">
                    <v-list-item-icon class="mr-3"><v-icon small>mdi-archive-arrow-up-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Reactivate</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-sheet>

    <ZoneEditDialog
      ref="editZone"
      :zone-types="zoneTypes"
      :facility-id="facilityId"
      :owners="owners"
      :levels="levels"
      @launch-viewer-2d="$emit('launch-viewer-2d', $event)"
      @saved="reload" />
    <ZoneRuleResolveDialog ref="resolveRule" />

    <!-- Zone types: pick one to filter the table, or add / edit types -->
    <v-dialog v-model="typesDialog" max-width="560" scrollable content-class="ds-wizard-dialog">
      <v-card class="ds-dialog types-dialog">
        <div class="ds-dialog__header">
          <div class="ds-dialog__header-icon">
            <v-icon color="primary">mdi-shape-outline</v-icon>
          </div>
          <div class="ds-dialog__header-text">
            <span class="ds-dialog__title">Zone types</span>
            <span class="ds-dialog__subtitle">
              {{ zoneTypes.length }} {{ zoneTypes.length === 1 ? 'type' : 'types' }} · choose one to filter the zones list
            </span>
          </div>
          <v-btn icon small title="Close" aria-label="Close" @click="typesDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="types-toolbar d-flex align-center">
          <v-text-field
            v-model="typeSearch"
            placeholder="Search types..."
            prepend-inner-icon="mdi-magnify"
            hide-details
            outlined
            dense
            clearable />
          <v-btn v-if="canManage" color="primary" @click="openType()">
            <v-icon left>mdi-plus</v-icon>Add type
          </v-btn>
        </div>

        <v-card-text class="types-body pa-0">
          <v-list dense class="py-1">
            <v-list-item-group :value="typeFilter" color="primary" mandatory>
              <v-list-item v-if="!typeSearch" :value="null" class="type-item" @click="pickType(null)">
                <v-list-item-avatar size="32" class="type-avatar">
                  <v-icon small>mdi-format-list-bulleted</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">All types</v-list-item-title>
                  <v-list-item-subtitle>Show every zone</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small>{{ countForType(null) }} zones</v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-list-item
                v-for="type in visibleTypes"
                :key="type.id"
                :value="type.id"
                class="type-item"
                @click="pickType(type.id)">
                <v-list-item-avatar size="32" class="type-avatar">
                  <span class="text-caption font-weight-bold">{{ type.name.charAt(0).toUpperCase() }}</span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ type.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="type.description">{{ type.description }}</v-list-item-subtitle>
                  <div class="d-flex flex-wrap mt-1" style="gap: 4px">
                    <v-chip v-if="!type.allowMultipleAssignments" x-small label color="warning" outlined>One zone per record</v-chip>
                    <v-chip v-for="name in typeAttributeNames(type)" :key="name" x-small label outlined>{{ name }}</v-chip>
                  </div>
                </v-list-item-content>
                <v-list-item-action class="flex-row align-center" style="gap: 4px">
                  <v-chip x-small>{{ countForType(type.id) }} zones</v-chip>
                  <v-btn v-if="canManage" icon small title="Edit type" :aria-label="`Edit ${type.name}`" @click.stop="openType(type)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <div v-if="!zoneTypes.length" class="types-empty text-center">
            <v-icon large class="mb-2">mdi-shape-plus-outline</v-icon>
            <div class="font-weight-medium">No zone types yet</div>
            <div class="caption mb-3">Add a type, for example Work Area or Fire Area, before creating zones.</div>
            <v-btn v-if="canManage" small color="primary" @click="openType()"><v-icon small left>mdi-plus</v-icon>Add type</v-btn>
          </div>
          <div v-else-if="!visibleTypes.length" class="types-empty text-center caption">
            No types match "{{ typeSearch }}".
          </div>
        </v-card-text>

        <div class="ds-dialog__footer">
          <span class="ds-dialog__hint">
            <template v-if="typeFilter">Filtering by {{ typeName(typeFilter) }}</template>
            <template v-else>Showing all types</template>
          </span>
          <v-spacer />
          <v-btn v-if="typeFilter" text @click="pickType(null)">Clear filter</v-btn>
          <v-btn color="primary" depressed @click="typesDialog = false">Done</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Retire now, or schedule it for a date -->
    <v-dialog v-model="retireDialog" max-width="440">
      <v-card v-if="retireZone">
        <v-card-title class="text-subtitle-1 font-weight-bold">Retire zone</v-card-title>
        <v-card-text>
          <p class="mb-3">
            "{{ retireZone.name }}" will be hidden from cards, Explorer and the viewer, and cannot take new
            assignments. Its {{ retireZone.assignmentCount }} existing links are kept and return if you reactivate it.
          </p>
          <v-radio-group v-model="retireWhen" hide-details class="mt-0">
            <v-radio label="Retire now" value="now" />
            <v-radio label="Retire on a date" value="date" />
          </v-radio-group>
          <v-menu
            v-if="retireWhen === 'date'"
            v-model="retireDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="retireOn ? formatDate(retireOn) : ''"
                label="Retirement date"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                class="mt-3"
                hint="The zone stays active until this date"
                persistent-hint
                v-bind="attrs"
                v-on="on" />
            </template>
            <v-date-picker v-model="retireOn" :min="tomorrow" no-title @input="retireDateMenu = false" />
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="retireDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="retireWhen === 'date' && !retireOn" :loading="retiring" @click="confirmRetire">
            {{ retireWhen === 'date' ? 'Schedule' : 'Retire' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Zone type editor: attributes are text or dropdown fields, like task type section fields -->
    <v-dialog v-model="typeDialog" max-width="600" scrollable content-class="ds-wizard-dialog">
      <v-card v-if="typeForm" class="ds-dialog type-form-dialog">
        <div class="ds-dialog__header">
          <div class="ds-dialog__header-icon">
            <v-icon color="primary">mdi-shape-outline</v-icon>
          </div>
          <div class="ds-dialog__header-text">
            <span class="ds-dialog__title">{{ typeForm.id ? 'Edit zone type' : 'New zone type' }}</span>
            <span class="ds-dialog__subtitle">Zones of this type carry the attributes below</span>
          </div>
          <v-btn icon small title="Close" aria-label="Close" @click="typeDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="type-form-body">
          <v-text-field v-model="typeForm.name" label="Name" outlined dense maxlength="200" />
          <v-textarea v-model="typeForm.description" label="Description" outlined dense rows="2" auto-grow />
          <v-switch
            v-model="typeForm.allowMultipleAssignments"
            inset
            dense
            class="mt-0 mb-2"
            label="A tag or task can be in several zones of this type"
            :hint="typeForm.allowMultipleAssignments
              ? 'Off: one zone per record; adding another asks before replacing it.'
              : 'Records already in several zones must be resolved before this applies.'"
            persistent-hint />

          <div class="d-flex align-center mb-2">
            <span class="font-weight-medium">Attributes</span>
            <v-spacer />
            <v-btn small text color="primary" @click="addTypeField"><v-icon small left>mdi-plus</v-icon>Add attribute</v-btn>
          </div>
          <div v-if="!typeForm.fields.length" class="caption mb-2">
            No attributes. Zones of this type have a name, description and colour only.
          </div>
          <div v-for="(field, index) in typeForm.fields" :key="field.key" class="type-field">
            <div class="d-flex align-start" style="gap: 8px">
              <v-text-field v-model="field.label" label="Attribute name" outlined dense hide-details maxlength="200" class="flex-grow-1" />
              <v-select v-model="field.fieldType" :items="fieldTypes" label="Type" outlined dense hide-details style="max-width: 140px" />
              <v-btn
                icon
                small
                class="mt-1"
                title="Remove attribute"
                :aria-label="`Remove ${field.label || 'attribute'}`"
                @click="typeForm.fields.splice(index, 1)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <v-combobox
              v-if="field.fieldType === 'dropdown'"
              v-model="field.values"
              label="Options"
              hint="Type an option and press Enter"
              persistent-hint
              multiple
              small-chips
              deletable-chips
              outlined
              dense
              class="mt-2" />
          </div>
        </v-card-text>
        <div class="ds-dialog__footer">
          <span class="ds-dialog__hint">Removing an attribute keeps the values zones already store.</span>
          <v-spacer />
          <v-btn text @click="typeDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :disabled="!isTypeFormValid" :loading="savingType" @click="saveType">Save</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
  import ZoneEditDialog from '../components/ZoneEditDialog.vue';
  import ZoneRuleResolveDialog from '../components/ZoneRuleResolveDialog.vue';
  import zonesService from '../services/zones.service';
  import { error } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';
  import { $has } from '@/utils';
  import { planUnavailableReason, isPlanTranslated } from '../utils/planLevels';
  import dayjs from 'dayjs';

  let fieldKey = 0;

  /**
   * Zones configuration (AVA-763), on two pages:
   * - Facility Admin > Zones (/:id/admin/zones): the facility's zones and its group's, with boundaries
   *   drawn on the facility's level plans.
   * - Company > Zones (/manage-zones): every zone of the facilities and groups the user can open, with
   *   an owner filter. A new zone belongs to one facility or one facility group; boundaries are typed.
   * Retired zones are listed by default ("Show retired"). Retiring can be immediate or scheduled for a
   * date. Retire/reactivate never touches assignments.
   */
  export default {
    name: 'ZonesAdmin',
    components: { ZoneEditDialog, ZoneRuleResolveDialog },
    // Supplied by the admin page (src/features/Admin/views/index.vue) like Levels & Plans.
    props: {
      levels: { type: Array, default: () => [] },
    },
    data() {
      return {
        loading: false,
        zoneTypes: [],
        zones: [],
        owners: { facilities: [], groups: [] },
        ownerFilter: null, // company admin: "facility:<id>" | "group:<id>" | null for all
        typeFilter: null,
        searchText: '',
        showRetired: true,
        typesDialog: false, // the Zone types list opens as a modal from its toolbar button
        typeSearch: '',
        retireDialog: false,
        retireZone: null,
        retireWhen: 'now', // 'now' | 'date'
        retireOn: null, // YYYY-MM-DD
        retireDateMenu: false,
        retiring: false,
        openingPlanId: null, // zone whose plan is being opened
        typeDialog: false,
        typeForm: null,
        savingType: false,
        fieldTypes: [
          { text: 'Text', value: 'text' },
          { text: 'Dropdown', value: 'dropdown' },
        ],
        headers: [
          { text: 'Zone', value: 'name' },
          { text: 'Type', value: 'zoneTypeId' },
          // Owner name as the value, so the column sorts and the search box matches it.
          { text: 'Belongs to', value: 'ownerName' },
          { text: 'Boundary', value: 'boundary', sortable: false },
          // Level name as the value, so the column sorts by level and the search box matches it.
          { text: '2D plan', value: 'planLevel', align: 'center' },
          { text: 'Records', value: 'assignmentCount' },
          { text: 'Status', value: 'status' },
          // Date it was retired, or the scheduled date; sorts as YYYY-MM-DD.
          { text: 'Retire date', value: 'retireDate' },
          { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
        ],
      };
    },
    computed: {
      // Facility Admin routes are /:id/admin/...; the company page has no facility.
      facilityId() {
        return this.$route.params.id || null;
      },
      isCompany() {
        return !this.facilityId;
      },
      ownerFilterItems() {
        const { facilities, groups } = this.owners;
        return [
          { text: 'All facilities and groups', value: null },
          ...(groups.length ? [{ header: 'Facility groups' }] : []),
          ...groups.map((g) => ({ text: g.name, value: `group:${g.id}`, icon: 'mdi-group' })),
          ...(facilities.length ? [{ header: 'Facilities' }] : []),
          ...facilities.map((f) => ({ text: f.name, value: `facility:${f.id}`, icon: 'mdi-domain' })),
        ];
      },
      canManage() {
        return $has(permissions.Zones.Manage);
      },
      isTypeFormValid() {
        const form = this.typeForm;
        if (!form?.name?.trim()) return false;
        return form.fields.every((f) => f.label.trim() && (f.fieldType !== 'dropdown' || f.values.length));
      },
      tableZones() {
        return this.zones
          .filter((z) => this.matchesOwner(z))
          .map((z) => ({
            ...z,
            planLevel: z.boundary?.plan?.levelName || '',
            retireDate: z.retiredOn || z.retireOn || '',
            ownerName: !this.isCompany && z.scope !== 'group' ? 'This facility' : z.scopeName,
          }))
          .filter((z) => (this.showRetired || z.status === 'active') && (!this.typeFilter || z.zoneTypeId === this.typeFilter));
      },
      visibleTypes() {
        const text = (this.typeSearch || '').toLowerCase().trim();
        if (!text) return this.zoneTypes;
        return this.zoneTypes.filter((t) => t.name.toLowerCase().includes(text) || (t.description || '').toLowerCase().includes(text));
      },
      tomorrow() {
        return dayjs().add(1, 'day').format('YYYY-MM-DD');
      },
    },
    mounted() {
      this.reload();
    },
    methods: {
      rowClass(item) {
        return [item.status === 'retired' ? 'retired-row' : '', this.canManage ? 'zone-row--editable' : ''].join(' ');
      },
      // A row opens the zone's editor for those who may edit it.
      openRow(item) {
        if (this.canManage) this.$refs.editZone.open(item);
      },
      /**
       * Opens the level the boundary was drawn on in the admin page's 2D side panel, as Levels & Plans
       * does. A group zone drawn in another facility has no level here.
       */
      openPlan(zone) {
        const levels = this.levels.length ? this.levels : this.$store.state.levels || [];
        const level = levels.find((l) => l.id === zone.boundary?.plan?.levelId);
        if (!level) {
          this.$dialog.notify.warning(`The ${zone.planLevel} plan is not a level of this facility.`);
          return;
        }
        const unavailable = planUnavailableReason(level, { forDrawing: false });
        if (unavailable) {
          this.$dialog.notify.warning(`${level.name}: ${unavailable}.`);
          return;
        }
        this.openingPlanId = zone.id;
        isPlanTranslated(level)
          .then((translated) => {
            if (translated) this.$emit('launch-viewer-2d', level);
            else this.$dialog.notify.warning('Plot plan not yet translated, please wait.');
          })
          .finally(() => {
            this.openingPlanId = null;
          });
      },
      typeAttributeNames(type) {
        return (type.fields || []).map((f) => f.label);
      },
      pickType(id) {
        this.typeFilter = id;
        this.typesDialog = false;
      },
      formatDate(date) {
        return dayjs(date).format('DD MMM YYYY');
      },
      typeName(id) {
        return this.zoneTypes.find((t) => t.id === id)?.name || '—';
      },
      countForType(id) {
        return this.zones.filter((z) => this.matchesOwner(z) && (!id || z.zoneTypeId === id) && (this.showRetired || z.status === 'active')).length;
      },
      // The company page's owner filter. A facility shows its own zones and its group's, as it would
      // list them itself.
      matchesOwner(zone) {
        if (!this.ownerFilter) return true;
        const [kind, id] = this.ownerFilter.split(':');
        if (kind === 'group') return zone.facilityGroupId === id;
        const group = this.owners.facilities.find((f) => f.id === id)?.facilityGroupId;
        return zone.facilityId === id || (!!group && zone.facilityGroupId === group);
      },
      ownerTitle(zone) {
        return zone.scope === 'group' ? `Shared by every facility in ${zone.scopeName}` : `Only ${zone.scopeName} lists this zone`;
      },
      async reload() {
        this.loading = true;
        try {
          const [types, zones, owners] = await Promise.all([
            zonesService.getZoneTypes(),
            // No facility on the company page: every zone the user can reach.
            zonesService.getZones({ includeRetired: true, facilityId: this.facilityId }),
            zonesService.getOwners(),
          ]);
          this.zoneTypes = types.data;
          this.zones = zones.data;
          this.owners = owners.data;
          // Keep the viewer palette in step with configuration changes.
          this.$store.dispatch('ZONES/REFRESH_ZONES').catch(() => {});
        } catch (err) {
          error('Failed to load zones', err);
          this.$dialog.notify.error('Failed to load zones');
        } finally {
          this.loading = false;
        }
      },
      openRetire(zone) {
        this.retireZone = zone;
        this.retireWhen = zone.retireOn ? 'date' : 'now';
        this.retireOn = zone.retireOn || null;
        this.retireDialog = true;
      },
      async confirmRetire() {
        const zone = this.retireZone;
        const retireOn = this.retireWhen === 'date' ? this.retireOn : null;
        this.retiring = true;
        try {
          await zonesService.setZoneStatus(zone.id, 'retired', { retireOn });
          this.$dialog.notify.success(retireOn ? `"${zone.name}" will retire on ${this.formatDate(retireOn)}` : `"${zone.name}" retired`);
          this.retireDialog = false;
          await this.reload();
        } catch (err) {
          this.$dialog.notify.error(err.message);
        } finally {
          this.retiring = false;
        }
      },
      // Reactivates a retired zone, or cancels a scheduled retirement.
      async setStatus(zone, status) {
        try {
          await zonesService.setZoneStatus(zone.id, status);
          const done = zone.retireOn ? 'scheduled retirement cancelled' : 'reactivated';
          this.$dialog.notify.success(`"${zone.name}" ${done}`);
          await this.reload();
        } catch (err) {
          this.$dialog.notify.error(err.message);
        }
      },
      openType(type = null) {
        // Dropdown options edit as plain values; their ids are kept on save so stored values stay valid.
        const fields = (type?.fields || []).map((f) => ({
          key: ++fieldKey,
          id: f.id,
          label: f.label,
          fieldType: f.fieldType,
          values: (f.options || []).map((o) => o.value),
          options: f.options || [],
        }));
        this.typeForm = {
          id: type?.id || null,
          name: type?.name || '',
          description: type?.description || '',
          allowMultipleAssignments: type ? type.allowMultipleAssignments !== false : true,
          fields,
        };
        this.typeDialog = true;
      },
      addTypeField() {
        this.typeForm.fields.push({ key: ++fieldKey, id: null, label: '', fieldType: 'text', values: [], options: [] });
      },
      async saveType() {
        const form = this.typeForm;
        const fields = form.fields.map((f) => ({
          id: f.id,
          label: f.label.trim(),
          fieldType: f.fieldType,
          options: f.fieldType === 'dropdown'
            ? f.values.map((value) => ({ id: f.options.find((o) => o.value === value)?.id || null, value: String(value).trim() }))
            : [],
        }));
        const payload = {
          id: form.id,
          name: form.name.trim(),
          description: form.description,
          allowMultipleAssignments: form.allowMultipleAssignments,
          fields,
        };
        this.savingType = true;
        try {
          await this.saveTypeResolvingRule(payload);
        } catch (err) {
          this.$dialog.notify.error(err.message);
        } finally {
          this.savingType = false;
        }
      },
      // Allowing only one zone is refused while records are in several; resolve them, then save again.
      async saveTypeResolvingRule(payload) {
        try {
          await zonesService.saveZoneType(payload);
        } catch (err) {
          const violations = err.status === 409 ? err.data?.violations : null;
          if (!violations) throw err;
          const restricted = await this.$refs.resolveRule.open(payload, violations);
          if (!restricted) return; // editor stays open; nothing was saved
          await zonesService.saveZoneType(payload);
        }
        this.$dialog.notify.success(`Zone type "${payload.name}" saved`);
        this.typeDialog = false;
        await this.reload();
      },
    },
  };
</script>

<style lang="scss">
  #admin-zones-container {
    .v-dialog__content {
      position: absolute !important;
    }

    .retired-row {
      opacity: 0.6;
    }

    .zone-row--editable {
      cursor: pointer;
    }

    .admin-subtitle {
      color: var(--v-text_secondary-base);
    }

    .owner-cell {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      white-space: nowrap;

      .v-icon {
        color: inherit;
      }
    }

    // Group zones read as shared at a glance.
    .owner-cell--group {
      color: var(--v-primary-base);
      font-weight: 500;
    }

    .swatch {
      width: 12px;
      height: 12px;
      border-radius: var(--ds-radius-full);
      flex: 0 0 12px;
    }
  }

  // The Zone types modal renders at the app root, outside #admin-zones-container.
  .types-dialog {
    // Header, toolbar and footer stay put; only the list scrolls.
    max-height: 80vh;

    .types-toolbar {
      gap: var(--ds-space-sm);
      padding: var(--ds-space-sm) var(--ds-space-lg);
      border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
    }

    .types-body {
      flex: 1 1 auto;
      min-height: 160px;
      max-height: 420px;
      overflow-y: auto;
    }

    .type-item {
      padding: 0 var(--ds-space-lg);
    }

    .type-avatar {
      background: var(--v-bg7-base);
      color: var(--v-text-base);
    }

    .types-empty {
      padding: var(--ds-space-xl, 32px) var(--ds-space-lg);
    }
  }

  .type-form-dialog {
    max-height: 85vh;

    .type-form-body {
      padding: var(--ds-space-md) var(--ds-space-lg) !important;
      overflow-y: auto;
    }

    .type-field {
      padding: var(--ds-space-sm);
      margin-bottom: var(--ds-space-sm);
      border: 1px solid var(--v-bg7-base);
      border-radius: var(--ds-radius-md);
    }
  }
</style>
